import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = none, object = logged in
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    // Auth now uses an httpOnly cookie set by the backend — we can't
    // see it from JS. The only way to know the user's auth state is
    // to call /auth/me and check the response. Returns 401 if no
    // valid cookie; we treat that as logged-out.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("auth.refresh: not authenticated", err?.response?.status);
      }
      // Wipe any legacy localStorage token so it can't leak again.
      localStorage.removeItem("ofunded_token");
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Auth cookie is set by the backend on this response. We no longer
    // mirror the token into localStorage — that was the XSS surface.
    // Wipe any legacy token that may still be there from a prior session.
    localStorage.removeItem("ofunded_token");
    setUser(data.user);
    return data.user;
  };

  // Google OAuth: exchange the auth code for our JWT.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = async (code, redirectUri) => {
    const { data } = await api.post("/auth/google/exchange", { code, redirect_uri: redirectUri });
    // Same as /login — backend sets the cookie; we just clean up any
    // legacy localStorage residue.
    localStorage.removeItem("ofunded_token");
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      // Backend clears the httpOnly cookie via Set-Cookie with max-age=0.
      await api.post("/auth/logout");
    } catch (err) {
      // Best-effort logout — backend may be unreachable or already invalidated.
      // We still clear local state below so the user is logged out in the UI.
      if (process.env.NODE_ENV !== "production") {
        console.debug("auth.logout: server call failed", err);
      }
    }
    localStorage.removeItem("ofunded_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, loginWithGoogle, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
